import {IAppBuilderParameterValueSourcePropsScreenshot} from "@AppBuilderLib/features/appbuilder";
import {PROCESS_STATUS} from "@AppBuilderLib/shared/config";
import {useShapeDiverStoreProcessManager} from "@AppBuilderLib/shared/model";
import {FLAG_TYPE} from "@shapediver/viewer.session";
import {
	OrthographicCameraProperties,
	PerspectiveCameraProperties,
} from "@shapediver/viewer.viewport";
import {useEffect, useRef, useState} from "react";
import {useShallow} from "zustand/react/shallow";
import {ViewportCreateDto} from "../config";
import {useShapeDiverStoreViewport} from "./useShapeDiverStoreViewport";
import {useShapeDiverStoreViewportAccessFunctions} from "./useShapeDiverStoreViewportAccessFunctions";
import {useShapeDiverViewportIconsStore} from "./useShapeDiverViewportIconsStore";
import {useViewportId} from "./useViewportId";

/**
 * Hook for creating a viewport of the ShapeDiver 3D Viewer.
 * Typically, you want to directly use the {@link ViewportComponent} instead
 * of calling this hook yourself.
 * @see {@link useShapeDiverStoreViewport} to access the API of the viewport.
 * @param props
 * @returns
 */
export function useViewport(props: ViewportCreateDto) {
	const {createViewport, closeViewport} = useShapeDiverStoreViewport(
		useShallow((state) => ({
			createViewport: state.createViewport,
			closeViewport: state.closeViewport,
		})),
	);
	const {addViewportAccessFunctions, removeViewportAccessFunctions} =
		useShapeDiverStoreViewportAccessFunctions();
	const {processManagers} = useShapeDiverStoreProcessManager(
		useShallow((state) => ({
			processManagers: state.processManagers,
		})),
	);

	const [error, setError] = useState<Error | undefined>(undefined);
	const promiseChain = useRef(Promise.resolve());
	const canvasRef = useRef(null);
	const {viewportId: defaultViewportId} = useViewportId();
	const _props = {...props, id: props.id ?? defaultViewportId};

	useEffect(() => {
		processManagersRef.current = processManagers;
	}, [processManagers]);
	const processManagersRef = useRef(processManagers);

	useEffect(() => {
		promiseChain.current = promiseChain.current.then(async () => {
			let flags: {[key: string]: FLAG_TYPE} | undefined = undefined;
			if (processManagersRef.current) {
				flags = {};
				for (const processManager of Object.values(
					processManagersRef.current,
				)) {
					if (
						processManager.status === PROCESS_STATUS.CREATED ||
						processManager.status === PROCESS_STATUS.RUNNING
					) {
						processManager.addFlag(_props.id, (flag: FLAG_TYPE) => {
							const token = Math.random()
								.toString(36)
								.substring(2, 15);
							flags![token] = flag;
							return token;
						});
					}
				}
			}

			const viewportApi = await createViewport(
				{
					canvas: canvasRef.current!,
					..._props,
					flags,
				},
				{
					onCreated: (viewport) => {
						const iconStore =
							useShapeDiverViewportIconsStore.getState();
						iconStore.initialize(viewport);
					},
					onError: setError,
				},
			);
			if (viewportApi && props.showStatistics)
				viewportApi.showStatistics = true;

			if (viewportApi)
				addViewportAccessFunctions(_props.id, {
					convertToGlTF: async () => {
						return viewportApi.convertToGlTF(undefined, true);
					},
					getScreenshot: async (
						props?: IAppBuilderParameterValueSourcePropsScreenshot,
					) => {
						const {contentType, quality, resolution, camera} =
							props || {};
						const screenshot =
							await viewportApi.getScreenshotAdvanced(
								contentType,
								quality,
								resolution,
								camera as
									| OrthographicCameraProperties
									| PerspectiveCameraProperties
									| undefined,
							);
						// sometimes the screenshot is not ready immediately (even though it should be)
						await new Promise((resolve) => setTimeout(resolve, 0));

						return screenshot;
					},
					addFlag: (flag: FLAG_TYPE) => {
						return viewportApi.addFlag(flag);
					},
					removeFlag: (token: string) =>
						viewportApi.removeFlag(token),
				});
		});

		return () => {
			promiseChain.current = promiseChain.current
				.then(() =>
					closeViewport(_props.id, {
						onClosed: (viewportId) => {
							const iconStore =
								useShapeDiverViewportIconsStore.getState();
							iconStore.clear(viewportId);
						},
					}),
				)
				.then(() => removeViewportAccessFunctions(_props.id));
		};
	}, [props.id]);

	return {
		canvasRef,
		error,
	};
}
